import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CardView } from '@lemoncloud/lemon-payments-api';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { CreditCard, Plus, X } from 'lucide-react';

import { cardQueryKeys, useBulkUpdateCards, useCards } from '@codes/payments';
import {
    Badge,
    Button,
    PageDialog,
    PageDialogClose,
    PageDialogContent,
    PageDialogHeader,
    PageDialogTitle,
    SharedSpinner,
    Sheet,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@codes/ui-kit';

import { CardRegistrationForm } from './CardRegistrationForm';
import { useDialogStore } from '../../stores';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY as string);

export const EnrollCardDialog = () => {
    const { t } = useTranslation();
    const { isCardDialogOpen, setCardDialog } = useDialogStore(state => state);
    const [isCardFormSheetOpen, setCardFormSheetOpen] = useState(false);

    const handleSuccess = (createdCard: CardView) => {
        setCardFormSheetOpen(false);
    };

    const {
        data: cardsData,
        isFetching: isCardsFetching,
        isRefetching: isCardsRefetching,
        isError,
        refetch,
    } = useCards({ stereo: 'stripe', view: 'mine' });

    const { mutateAsync: bulkUpdateCards, isPending: isBulkUpdateCardsPending } = useBulkUpdateCards();

    const [defCardId, setDefCartId] = useState<string | null>(null);

    const queryClient = useQueryClient();
    const onCompleted = async () => {
        if (!cardsData || cardsData?.total === 0 || !defCardId) {
            return;
        }

        if (defCardId === cardsData.list.find(card => card.isDefault)?.id) {
            setCardDialog(false);
            return;
        }

        const list = cardsData.list.map(card => ({ ...card, isDefault: card.id === defCardId }));

        const updatedCards = await bulkUpdateCards({ list });

        queryClient.setQueryData(cardQueryKeys.list({ stereo: 'stripe', view: 'mine' }), (oldData: any) => {
            if (!oldData) {
                return oldData;
            }
            return {
                ...oldData,
                data: updatedCards,
                list: updatedCards,
            };
        });

        setCardDialog(false);
    };

    const onCardClick = (cardId: string | undefined) => {
        if (!cardId) {
            return;
        }

        if (cardId === defCardId) {
            setDefCartId(null);
            return;
        }
        setDefCartId(cardId);
    };

    const ContentView = useMemo(() => {
        const isLoading = isCardsFetching || isCardsRefetching;

        if (isLoading) {
            return (
                <div className="px-6 mt-5">
                    <div className="w-8 h-8 bg-default rounded animate-pulse" />
                    <div className="h-6 bg-default rounded animate-pulse mt-[10px] mb-6" />
                    <div className="flex items-center gap-3 overflow-auto">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="min-w-[190px] h-[130px] bg-default rounded-2xl animate-pulse" />
                        ))}
                    </div>
                    <div className="mt-3 space-y-2">
                        <div className="h-4 bg-default rounded animate-pulse" />
                        <div className="h-4 bg-default rounded animate-pulse w-3/4" />
                    </div>
                    <div className="h-12 bg-default rounded animate-pulse mt-[54px]" />
                </div>
            );
        }

        if (isError) {
            return (
                <div className="px-6 mt-5 text-center">
                    <div className="text-error mb-4">{t('myPage.failedToCards')}</div>
                    <Button onClick={() => refetch()} variant="outline">
                        {t('myPage.retry')}
                    </Button>
                </div>
            );
        }

        return (
            <div className="px-6 mt-5 relative">
                {isCardsRefetching && (
                    <div className="absolute inset-0 bg-card/50 flex items-center justify-center z-10">
                        <div className="w-6 h-6 border-2 border-border border-t-text rounded-full animate-spin" />
                    </div>
                )}
                <CreditCard size={32} />
                <div className="text-lg text-text mt-[10px] font-medium mb-6">{t('myPage.choosePaymentMethod')}</div>
                <div className="flex items-center gap-3 overflow-auto">
                    <button
                        onClick={() => setCardFormSheetOpen(true)}
                        className="min-w-[190px] h-[130px] w-full border-[0.6px] border-text flex flex-col items-center justify-center gap-2 text-text rounded-2xl hover:border-point"
                    >
                        <Plus size={24} />
                        <div className="text-lg font-medium">{t('myPage.addPaymentMethod')}</div>
                    </button>

                    {cardsData?.list?.map(card => (
                        <button
                            key={card.id}
                            className={`min-w-[190px] h-[130px] relative w-full border-[0.6px] border-text flex flex-col items-center justify-center text-text rounded-2xl hover:border-point ${
                                defCardId === card.id ? 'border-gradient' : ''
                            }`}
                            onClick={() => onCardClick(card.id)}
                        >
                            {card.isDefault && <Badge className="absolute top-2 right-2">{t('myPage.default')}</Badge>}
                            <div className="flex gap-x-2 items-center">
                                <CreditCard size={24} />
                                <div className="text-lg font-medium">{card.brandEn}</div>
                            </div>
                            <div className="text-lg font-medium">**** {card.last4}</div>
                        </button>
                    ))}
                </div>
                <div className="mt-3 text-sm text-text-500">
                    {t('myPage.cardDataPrivacy')}
                    <br></br>
                    {t('myPage.stripeManaged')}
                </div>
                <Button
                    onClick={onCompleted}
                    size="lg"
                    className="w-full mt-[54px]"
                    disabled={isBulkUpdateCardsPending || !defCardId}
                >
                    {isBulkUpdateCardsPending ? <SharedSpinner /> : t('myPage.completed')}
                </Button>
            </div>
        );
    }, [cardsData?.list, defCardId, isBulkUpdateCardsPending, isCardsFetching, isCardsRefetching, isError, refetch]);

    return (
        <PageDialog open={isCardDialogOpen} onOpenChange={setCardDialog}>
            <PageDialogContent>
                <div className="w-full max-w-[500px] sm:pt-[56px] sm:pb-[34px]">
                    <PageDialogHeader>
                        <PageDialogTitle>{t('myPage.registerCard')}</PageDialogTitle>
                        <PageDialogClose asChild>
                            <button className="absolute right-6 top-[19px] z-[51]" type="button">
                                <X className="w-5 h-5" />
                            </button>
                        </PageDialogClose>
                    </PageDialogHeader>

                    {ContentView}
                    <Sheet open={isCardFormSheetOpen} onOpenChange={setCardFormSheetOpen}>
                        <SheetContent className="overflow-hidden" aria-describedby={undefined}>
                            <SheetTitle />
                            <SheetHeader className="justify-end">
                                <SheetClose>
                                    <X className="w-5 h-5" />
                                </SheetClose>
                            </SheetHeader>
                            <Elements stripe={stripePromise}>
                                <CardRegistrationForm onSuccess={handleSuccess} />
                            </Elements>
                        </SheetContent>
                    </Sheet>
                </div>
            </PageDialogContent>
        </PageDialog>
    );
};
